<script lang="ts">
  import { onDestroy } from "svelte";
  import type { GameServer } from "@/api";
  import { coreOptions, gameLabel, statusLabels } from "@/config";
  import { routeHref } from "@/router";
  import { copyToClipboard } from "@/utils/clipboard";
  import { serverActionAvailability, type ServerAction } from "@/server-state-policy";

  const {
    server,
    busyActions = {},
    onAction,
  }: {
    server: GameServer;
    busyActions?: Partial<Record<ServerAction, boolean>>;
    onAction: (action: ServerAction) => void | Promise<void>;
  } = $props();
  let copied = $state(false);
  let copyError = $state("");
  let copying = $state(false);
  let copyTimer: ReturnType<typeof setTimeout> | undefined;
  const availability = $derived(serverActionAvailability(server));
  const actionBusy = $derived(Object.values(busyActions).some(Boolean));
  const killBlocked = $derived(Boolean(busyActions.kill));
  const actions: Array<{ action: ServerAction; label: string }> = [
    { action: "start", label: "Запустить" },
    { action: "restart", label: "Перезагрузить" },
    { action: "stop", label: "Остановить" },
    { action: "kill", label: "Убить сервер" },
  ];
  const coreLabel = $derived(
    coreOptions.find((option) => option.value === server.core)?.label ?? server.core,
  );

  $effect(() => {
    void server.id;
    void server.joinAddress;
    copied = false;
    copyError = "";
    clearTimeout(copyTimer);
  });
  onDestroy(() => clearTimeout(copyTimer));

  async function copyAddress() {
    if (copying) {
      return;
    }
    const address = server.joinAddress;
    copying = true;
    copyError = "";
    try {
      const result = await copyToClipboard(address);
      if (server.joinAddress !== address) {
        return;
      }
      copied = result;
      if (result) {
        clearTimeout(copyTimer);
        copyTimer = setTimeout(() => (copied = false), 2_000);
      } else {
        copyError = "Не удалось скопировать адрес. Выделите его и скопируйте вручную.";
      }
    } finally {
      copying = false;
    }
  }
</script>

<header class="server-header">
  <div class="server-identity">
    <a class="back-link" href={routeHref("servers")}>← Все серверы</a>
    <p class="eyebrow">Сервер / {coreLabel}</p>
    <h1>{server.name}</h1>
    <div class="server-description">
      <span>{gameLabel(server.game)} · {server.version}</span>
      <span class={`server-status ${server.status}`}
        ><i aria-hidden="true"></i>{statusLabels[server.status] ?? server.status}</span
      >
    </div>
  </div>
  <div class="server-controls" role="group" aria-label="Управление сервером">
    {#each actions as item (item.action)}
      <button
        class={item.action === "kill"
          ? "ghost-button danger server-control"
          : "button server-control"}
        type="button"
        disabled={!availability[item.action] || (item.action === "kill" ? killBlocked : actionBusy)}
        onclick={() => onAction(item.action)}
      >
        {busyActions[item.action] ? "Отправляем…" : item.label}
      </button>
    {/each}
  </div>
</header>

<section class="server-access" aria-label="Подключение к серверу">
  <div class="address">
    <span class="access-label">Адрес подключения</span>
    <strong>{server.joinAddress}</strong>
  </div>
  <button class="ghost-button copy-address" type="button" disabled={copying} onclick={copyAddress}
    >{copying ? "Копируем…" : copied ? "Скопировано" : "Скопировать адрес"}</button
  >
</section>
{#if copyError}<p class="form-error" role="alert">{copyError}</p>{/if}
{#if copied}<span class="copy-status" role="status">Адрес подключения скопирован</span>{/if}

<style>
  .server-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.25rem;
    margin-bottom: 1.25rem;
  }
  .server-identity {
    min-width: 0;
  }
  h1 {
    margin: 0.65rem 0;
    color: var(--cream);
    font: 500 clamp(1.8rem, 4vw, 3.5rem)/1.15 var(--font-display);
    letter-spacing: -0.05em;
    overflow-wrap: anywhere;
  }
  .server-description {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.7rem 1rem;
    color: var(--muted);
    font-size: 0.9rem;
  }
  .server-description :global(.server-status) {
    margin: 0;
    font-size: 0.8rem;
  }
  .server-controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
  }
  .server-control {
    white-space: nowrap;
  }
  .server-access {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;
    padding: 1rem 1.25rem;
    border: 1px solid var(--line-strong);
    background: var(--surface);
  }
  .address {
    display: grid;
    gap: 0.35rem;
    min-width: 0;
  }
  .access-label {
    color: var(--muted);
    font-size: 0.8rem;
  }
  strong {
    overflow-wrap: anywhere;
    font-size: 1rem;
  }
  .copy-address {
    border: 1px solid var(--line-strong);
    color: var(--cream);
  }
  .copy-status {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  @media (max-width: 640px) {
    .server-header {
      align-items: stretch;
      flex-direction: column;
      gap: 1rem;
    }
    .server-access {
      grid-template-columns: minmax(0, 1fr);
      padding: 1rem;
      gap: 0.75rem;
    }
    .server-controls {
      justify-content: flex-start;
    }
    .copy-address {
      justify-self: stretch;
    }
  }
</style>
